/**
 * PREVIEW — Discord Monitor (roadmap concept, not built).
 *
 * A community-intelligence mockup: what an agent watching Gary's Discord could
 * surface — activity rhythm, trending discussion topics, questions worth Gary
 * or a mod answering, and community tone. All data here is hardcoded sample
 * data; see components/preview/primitives.tsx for the honest-labelling banner.
 */
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Panel } from "../../components/salience/Panel";
import {
  BarRow,
  PreviewBanner,
  PreviewHeader,
  StatTile,
} from "../../components/preview/primitives";

// ---- Sample data (illustrative only) --------------------------------------

const ACTIVITY = [
  { h: "00", msgs: 41 },
  { h: "03", msgs: 18 },
  { h: "06", msgs: 27 },
  { h: "09", msgs: 88 },
  { h: "12", msgs: 134 },
  { h: "15", msgs: 156 },
  { h: "18", msgs: 213 },
  { h: "21", msgs: 178 },
];

const TOPICS = [
  { label: "UK budget / fiscal", value: 312, colour: "var(--color-agenda-elite)" },
  { label: "Housing & rent", value: 244, colour: "var(--color-agenda-public)" },
  { label: "The Trading Game", value: 187, colour: "var(--color-agenda-media)" },
  { label: "Wealth tax mechanics", value: 156, colour: "var(--color-agenda-search)" },
  { label: "Career / finance advice", value: 98, colour: "var(--color-up)" },
];

const QUESTIONS = [
  {
    user: "tom_w",
    channel: "#economics-chat",
    q: "If a wealth tax causes capital flight, doesn't that just shrink the base it's meant to tax?",
    upvotes: 34,
    age: "2h",
  },
  {
    user: "priya.k",
    channel: "#ask-gary",
    q: "Gary mentioned the 2008 collateral mechanics in the last video — is there a primer somewhere for the bond-collateral bit?",
    upvotes: 27,
    age: "5h",
  },
  {
    user: "deano88",
    channel: "#economics-chat",
    q: "Genuine question: how is this different from the mansion tax that got dropped in 2015?",
    upvotes: 19,
    age: "8h",
  },
];

const SENTIMENT = [
  { label: "Supportive", value: 62, colour: "var(--color-sentiment-supportive)" },
  { label: "Neutral", value: 29, colour: "var(--color-sentiment-neutral)" },
  { label: "Unsupportive", value: 9, colour: "var(--color-sentiment-unsupportive)" },
];

export function DiscordPreview() {
  const topicMax = Math.max(...TOPICS.map((t) => t.value));
  const sentTotal = SENTIMENT.reduce((s, x) => s + x.value, 0);

  return (
    <div>
      <PreviewBanner module="Discord Monitor" />
      <PreviewHeader
        kicker="future module · agent 3"
        title="Discord Monitor"
        blurb="Community intelligence from Gary's Discord — what the most engaged members are discussing, the questions worth a reply, and how the room is feeling, without anyone scrolling 2,000 messages a day."
      />

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile kicker="Server" value="14,302" label="members" sub="+184 this week" />
        <StatTile kicker="24h" value="1,047" label="messages" accent="up" sub="▲ 12% vs avg" />
        <StatTile kicker="Awaiting" value="7" label="questions flagged" sub="3 high-upvote" />
        <StatTile kicker="Tone · 24h" value="62%" label="supportive" accent="up" />
      </div>

      <Panel
        title="Activity rhythm — last 24h"
        eyebrow="When the room is awake"
        hint="Messages per 3-hour bucket. A real build would let you set quiet-hours alerting and spot when a topic suddenly heats up."
        className="mb-6"
      >
        <div className="relative h-[220px] w-full">
          <div className="absolute inset-0">
          <ResponsiveContainer>
            <AreaChart data={ACTIVITY} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="discord-act" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--color-agenda-public)" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="var(--color-agenda-public)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.07)" vertical={false} />
              <XAxis
                dataKey="h"
                stroke="#5a5f9e"
                tick={{ fontSize: 10, fill: "#9aa0d8" }}
                tickLine={false}
                tickFormatter={(h) => `${h}:00`}
              />
              <YAxis
                stroke="#5a5f9e"
                tick={{ fontSize: 10, fill: "#9aa0d8" }}
                tickLine={false}
                axisLine={false}
                width={32}
              />
              <Tooltip
                contentStyle={{
                  background: "#21246a",
                  border: "1px solid rgba(255,255,255,0.15)",
                  borderRadius: 8,
                  fontSize: 12,
                  color: "#eef0ff",
                }}
                labelFormatter={(h) => `${h}:00 UTC`}
                formatter={(v: number) => [v, "messages"]}
              />
              <Area
                type="monotone"
                dataKey="msgs"
                stroke="var(--color-agenda-public)"
                strokeWidth={2}
                fill="url(#discord-act)"
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
          </div>
        </div>
      </Panel>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Panel
          title="Questions worth answering"
          eyebrow="Surfaced by the agent"
          hint="Substantive, upvoted questions the agent judged worth Gary's or a mod's time — ranked, deduped, with the noise filtered out."
        >
          <ul className="space-y-2.5">
            {QUESTIONS.map((q, i) => (
              <li
                key={i}
                className="rounded-lg border border-white/10 bg-terminal-panel p-3"
              >
                <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-terminal-ink-muted">
                  <span className="font-mono text-agenda-public">@{q.user}</span>
                  <span className="truncate">{q.channel}</span>
                  <span className="ml-auto rounded bg-up/15 px-1.5 py-0.5 font-mono text-up">
                    ▲ {q.upvotes}
                  </span>
                  <span>{q.age}</span>
                </div>
                <p className="wrap-break-word text-[13px] leading-snug text-terminal-ink">
                  {q.q}
                </p>
              </li>
            ))}
          </ul>
        </Panel>

        <div className="flex flex-col gap-6">
          <Panel
            title="Trending topics — 7 days"
            eyebrow="What the room is talking about"
            hint="Clustered from message content. A spike here is an early read on what the audience wants next."
          >
            <div className="space-y-2.5">
              {TOPICS.map((t) => (
                <BarRow
                  key={t.label}
                  label={t.label}
                  value={t.value}
                  max={topicMax}
                  colour={t.colour}
                />
              ))}
            </div>
          </Panel>

          <Panel
            title="Community tone"
            eyebrow="Reuses agent-classy"
            hint="The same sentiment classifier the YouTube module runs, pointed at Discord — supportive / neutral / unsupportive."
          >
            <div className="flex h-3 w-full overflow-hidden rounded-full">
              {SENTIMENT.map((s) => (
                <div
                  key={s.label}
                  style={{ width: `${(100 * s.value) / sentTotal}%`, background: s.colour }}
                />
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-[12px]">
              {SENTIMENT.map((s) => (
                <span key={s.label} className="flex items-center gap-1.5 text-terminal-ink-muted">
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-sm"
                    style={{ background: s.colour }}
                  />
                  {s.label} <span className="font-mono text-terminal-ink">{s.value}%</span>
                </span>
              ))}
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}
